import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StateSliceName } from 'state/StateSliceName';
import { ICurrentCareWizardStep } from './types';
import { initialCareWizardDraft } from './utils/draft';

interface CareWizardStepsState {
  careWizardDraft: ICurrentCareWizardStep;
}

const initialState: CareWizardStepsState = {
  careWizardDraft: initialCareWizardDraft,
};

const careWizardStepsSlice = createSlice({
  name: StateSliceName.CareWizardSteps,
  initialState,
  reducers: {
    resetCareWizardDraft: (state) => {
      Object.assign(state.careWizardDraft, initialState.careWizardDraft);
    },
    updateCareWizardDraft: (state, action: PayloadAction<Partial<ICurrentCareWizardStep>>) => {
      Object.assign(state.careWizardDraft, action.payload);
    },
    updateCurrentStep: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.currentStep = action.payload;
    },
    updateOriginalInvoiceCost: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.originalInvoiceCost = action.payload;
    },
    updateTotalInvoiceSavings: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.totalInvoiceSavings = action.payload;
    },
    updatePaymentDue: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.paymentDue = action.payload;
    },
    updateAdditionalDiscountValue: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.additionalDiscountValue = action.payload;
    },
    updateRemainingExamCredits: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.remainingExamCredits = action.payload;
    },
    updateRemainingCarePassValue: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.remainingCarePassValue = action.payload;
    },
    updateExamAmountToConsume: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.examAmountToConsume = action.payload;
    },
    updateExamValueToConsume: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.examValueToConsume = action.payload;
    },
    updateCarePassValueToConsume: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.carePassValueToConsume = action.payload;
    },
    updateAdditionalDiscountValueToConsume: (state, action: PayloadAction<number>) => {
      state.careWizardDraft.additionalDiscountValueToConsume = action.payload;
    },
    updateShowPercentDiscountForm: (state, action: PayloadAction<boolean>) => {
      state.careWizardDraft.showPercentDiscountForm = action.payload;
    },
    updateIncludedExams: (
      state,
      action: PayloadAction<
        {
          type: string;
          value: string;
          benefitId: string;
        }[]
      >,
    ) => {
      state.careWizardDraft.includedExams = action.payload;
    },
    updateIsBalanceSelected: (state, action: PayloadAction<boolean>) => {
      state.careWizardDraft.isBalanceSelected = action.payload;
    },
    updateSelectedInvoiceId: (state, action: PayloadAction<string | null>) => {
      state.careWizardDraft.selectedInvoiceId = action.payload;
    },
    updateCareAccountCreditIntentId: (state, action: PayloadAction<string | null>) => {
      state.careWizardDraft.careAccountCreditIntentId = action.payload;
    },
    updateDraftExamTotals: (state, action: PayloadAction<Record<string, number>>) => {
      state.careWizardDraft.draftExamTotals = action.payload;
    },
  },
});

export const {
  resetCareWizardDraft,
  updateCareWizardDraft,
  updateCurrentStep,
  updateOriginalInvoiceCost,
  updateTotalInvoiceSavings,
  updatePaymentDue,
  updateAdditionalDiscountValue,
  updateRemainingExamCredits,
  updateRemainingCarePassValue,
  updateExamAmountToConsume,
  updateExamValueToConsume,
  updateCarePassValueToConsume,
  updateAdditionalDiscountValueToConsume,
  updateShowPercentDiscountForm,
  updateIncludedExams,
  updateIsBalanceSelected,
  updateSelectedInvoiceId,
  updateCareAccountCreditIntentId,
  updateDraftExamTotals,
} = careWizardStepsSlice.actions;

export default careWizardStepsSlice.reducer;
